import React from 'react'
import { Image, Linking, StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Icon, Text, type IconName } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import { eventPriceLabel, formatEventDateRange, getCommonStyles } from '@utils'
import type { EventOrganizer, KtEvent } from '@hooks'
import { EventAvatar } from './EventAvatar'
import { VenueMap } from './VenueMap'

interface EventHeroProps {
  event: KtEvent
  organizer: EventOrganizer | null
  compact?: boolean
}

/**
 * Top of every event page: cover (image or brand gradient), phase chip, name,
 * date, venue, organizer and the seat counter.
 */
export function EventHero({ event, organizer, compact = false }: EventHeroProps) {
  const theme = useTheme()
  const { t, currentLanguage } = useLanguage()
  const styles = getStyles(theme, compact)

  const phaseColor =
    event.phase === 'ongoing'
      ? theme.colors.green
      : event.phase === 'ended'
        ? theme.colors.greyBlue
        : theme.colors.primary
  const hasCoordinates = typeof event.latitude === 'number' && typeof event.longitude === 'number'
  const addressLine = [event.address, event.city].filter(Boolean).join(', ')
  const seats =
    event.capacity !== null
      ? t('events.attendeesCountWithCapacity', {
          count: event.attendeeCount,
          capacity: event.capacity,
        })
      : t('events.attendeesCount', { count: event.attendeeCount })

  return (
    <View style={styles.card}>
      <View style={styles.cover}>
        {event.coverImageUrl ? (
          <Image
            source={{ uri: event.coverImageUrl }}
            style={styles.coverImage}
            resizeMode="cover"
          />
        ) : (
          <View style={[styles.coverImage, styles.coverFallback]}>
            <Icon name="calendarStar" size={compact ? 40 : 56} color={theme.colors.white} />
          </View>
        )}
        <View style={[styles.phaseChip, { backgroundColor: phaseColor }]}>
          <Text type="bodyExtraSmallBold" color={theme.colors.white}>
            {t(`events.phase.${event.phase}`)}
          </Text>
        </View>
        <View
          style={[styles.priceChip, event.priceCents === null && styles.priceChipFree]}
          testID="event-price"
        >
          <Text
            type="bodySmallBold"
            color={event.priceCents === null ? theme.colors.green : theme.colors.white}
          >
            {eventPriceLabel(event, t('events.price.free'), currentLanguage)}
          </Text>
        </View>
      </View>

      <View style={styles.body}>
        <Text type={compact ? 'titleBold' : 'titleBigBold'} style={styles.title}>
          {event.name}
        </Text>

        {event.category || event.format ? (
          <View style={styles.chips}>
            {event.category ? (
              <View style={styles.categoryChip} testID="event-category-chip">
                <Text type="bodyExtraSmallBold" color={theme.colors.primary}>
                  {t(`events.category.${event.category}`)}
                </Text>
              </View>
            ) : null}
            <View style={styles.formatChip} testID="event-format-chip">
              <Text type="bodyExtraSmallBold" color={theme.colors.secondary}>
                {t(`events.format.${event.format ?? 'in_person'}`)}
              </Text>
            </View>
          </View>
        ) : null}

        <View style={styles.metaRow}>
          <Icon name="calendar" size={18} color={theme.colors.primary} />
          <Text type="bodyBold" color={theme.colors.black} style={styles.metaText}>
            {formatEventDateRange(event.startsAt, event.endsAt, currentLanguage)}
          </Text>
        </View>
        {event.location || addressLine ? (
          <View style={styles.metaRow}>
            <Icon name="mapPin" size={18} color={theme.colors.primary} />
            <View style={styles.metaText}>
              {event.location ? (
                <Text type="body" color={theme.colors.secondary}>
                  {event.location}
                </Text>
              ) : null}
              {addressLine ? (
                <Text type="bodySmall" color={theme.colors.greyBlue} testID="event-address">
                  {addressLine}
                </Text>
              ) : null}
            </View>
          </View>
        ) : null}
        {hasCoordinates ? (
          <View style={styles.mapBlock}>
            <VenueMap
              latitude={event.latitude as number}
              longitude={event.longitude as number}
              height={compact ? 140 : 180}
            />
            <LinkRow
              icon="map"
              label={t('events.venue.directions')}
              url={`https://www.google.com/maps/dir/?api=1&destination=${event.latitude},${event.longitude}`}
              testID="event-directions-link"
            />
          </View>
        ) : null}
        {/* Once the event is over the link leads nowhere useful. */}
        {event.onlineUrl && event.phase !== 'ended' ? (
          <LinkRow
            icon="video"
            label={t('events.details.joinOnline')}
            url={event.onlineUrl}
            testID="event-online-link"
          />
        ) : null}
        {event.websiteUrl ? (
          <LinkRow
            icon="globe"
            label={t('events.details.website')}
            url={event.websiteUrl}
            testID="event-website-link"
          />
        ) : null}
        {event.contactEmail ? (
          <LinkRow
            icon="message"
            label={event.contactEmail}
            url={`mailto:${event.contactEmail}`}
            testID="event-contact-link"
          />
        ) : null}

        <View style={styles.footer}>
          {organizer ? (
            <View style={styles.organizer}>
              <EventAvatar firstName={organizer.name} avatarUrl={organizer.avatarUrl} size={32} />
              <Text type="bodySmall" color={theme.colors.secondary} style={styles.metaText}>
                {t('events.organizedBy', { name: organizer.name })}
              </Text>
            </View>
          ) : (
            <View />
          )}
          <View style={[styles.seatPill, event.isFull && styles.seatPillFull]}>
            <Icon
              name="people"
              size={16}
              color={event.isFull ? theme.colors.error : theme.colors.primary}
            />
            <Text
              type="bodySmallBold"
              color={event.isFull ? theme.colors.error : theme.colors.primary}
              style={styles.seatText}
            >
              {event.isFull ? t('events.full') : seats}
            </Text>
          </View>
        </View>

        {!compact && event.description ? (
          <Text type="body" color={theme.colors.secondary} style={styles.description}>
            {event.description}
          </Text>
        ) : null}
      </View>
    </View>
  )
}

function LinkRow({
  icon,
  label,
  url,
  testID,
}: {
  icon: IconName
  label: string
  url: string
  testID: string
}) {
  const theme = useTheme()
  const styles = getStyles(theme, false)
  return (
    <TouchableOpacity
      accessibilityRole="link"
      onPress={() => void Linking.openURL(url)}
      style={styles.metaRow}
      testID={testID}
    >
      <Icon name={icon} size={18} color={theme.colors.primary} />
      <Text type="bodyBold" color={theme.colors.primary} style={styles.metaText}>
        {label}
      </Text>
    </TouchableOpacity>
  )
}

const getStyles = (theme: Theme, compact: boolean) =>
  StyleSheet.create({
    card: {
      backgroundColor: theme.colors.white,
      borderRadius: theme.border.jumbo,
      overflow: 'hidden',
      borderWidth: 1,
      borderColor: theme.colors.grey,
      ...getCommonStyles(theme).shadow,
    },
    cover: { height: compact ? 120 : 200, position: 'relative' },
    coverImage: { width: '100%', height: '100%' },
    coverFallback: {
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.primary,
    },
    phaseChip: {
      position: 'absolute',
      top: theme.spacing.lg,
      left: theme.spacing.lg,
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.xs,
      borderRadius: theme.border.jumbo,
    },
    priceChip: {
      position: 'absolute',
      top: theme.spacing.lg,
      right: theme.spacing.lg,
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.xs,
      borderRadius: theme.border.jumbo,
      backgroundColor: theme.colors.primary,
    },
    priceChipFree: { backgroundColor: theme.colors.white },
    body: { padding: compact ? theme.spacing.lg : theme.spacing.xxl, gap: theme.spacing.sm },
    title: { marginBottom: theme.spacing.xs },
    chips: { flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing.xs },
    categoryChip: {
      backgroundColor: theme.colors.sidebar,
      borderRadius: theme.border.jumbo,
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.xs,
    },
    formatChip: {
      borderWidth: 1,
      borderColor: theme.colors.grey,
      borderRadius: theme.border.jumbo,
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.xs,
    },
    mapBlock: { gap: theme.spacing.sm, marginVertical: theme.spacing.xs },
    metaRow: { flexDirection: 'row', alignItems: 'center' },
    metaText: { marginLeft: theme.spacing.sm, flexShrink: 1 },
    footer: {
      marginTop: theme.spacing.md,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: theme.spacing.md,
    },
    organizer: { flexDirection: 'row', alignItems: 'center', flexShrink: 1 },
    seatPill: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: theme.colors.sidebar,
      borderRadius: theme.border.jumbo,
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.xs + 2,
    },
    seatPillFull: { backgroundColor: theme.colors.errorSoft },
    seatText: { marginLeft: theme.spacing.xs },
    description: { marginTop: theme.spacing.md, lineHeight: 22 },
  })
